import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { NotificationDrawerProvider } from '../components/notification-drawer.js';
import { RefetchIndicator } from '../components/refetch-indicator.js';
import { RouterProgress } from '../components/router-progress.js';
import { useAppHeight } from '../lib/use-app-height.js';

/**
 * Router context — every route's `beforeLoad` receives this as `context`.
 * The QueryClient lives here so loaders can call `ensureQueryData(...)`
 * and share a single cache with components that subscribe via `useQuery`.
 * Wired up at the createRouter call in main.tsx.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

// RouterProgress lives here (not in main.tsx) because it calls
// `useRouterState`, which requires the RouterProvider context — only
// present once the route tree starts rendering. Mounting it as a sibling
// of RouterProvider crashed in production with `null.stores`.
export const Route = createRootRouteWithContext<RouterContext>()({
  // Without this the app had NO error boundary anywhere, so a single
  // component throwing during render unmounted the entire tree and the
  // founder got a blank page — background colour, no nav, nothing to act
  // on and nothing to report. That is how a broken card becomes a
  // whiteout. It degrades to something readable instead, and says the one
  // thing that actually helps us: what threw.
  errorComponent: RootError,
  component: RootShell,
});

function RootShell() {
  // Publishes --app-h (the real visible viewport height) for every shell
  // that pins a composer to the bottom. Root is the only place it can
  // live: one listener for the whole app, mounted before any route.
  useAppHeight();
  return (
    // NotificationDrawerProvider wraps the whole tree so the bell (top
    // nav, every page) and the /notifications inbox can open the same
    // global drawer. It lives here — inside RouterProvider — so the
    // drawer's CTA can navigate via useNavigate.
    <NotificationDrawerProvider>
      <RouterProgress />
      <RefetchIndicator />
      <Outlet />
    </NotificationDrawerProvider>
  );
}

/** Last-resort screen for anything that throws past a route's own
 *  handling. Deliberately plain: no brand chrome, no data fetching, no
 *  hooks that could themselves fail — an error screen that can break is
 *  worse than none. That is also why the copy is NOT wrapped in <Trans>:
 *  if the i18n provider is what threw, a translated error screen throws
 *  too and we are back to a blank page. Untranslated English here is a
 *  deliberate trade, not an oversight — and it keeps these strings out of
 *  the catalog, where a missing placeholder has bitten us before.
 *
 *  Mag's voice, because the founder is still the reader, but the message
 *  text is shown verbatim so it can be pasted to us. */
function RootError({ error }: { error: Error }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[var(--cream)] px-5">
      <div className="w-full max-w-[520px] rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--pulse)]">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
          ↘ something broke
        </p>
        <h1 className="mt-1 font-display text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
          that page didn't load.
        </h1>
        <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/70">
          this one's on me, not on you — nothing you did caused it and nothing you saved is lost.
          reload and it usually comes back.
        </p>
        {error?.message ? (
          <pre className="mt-4 max-h-[160px] overflow-auto rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3 font-mono text-[11px] leading-[1.45] text-[var(--ink)]/70 whitespace-pre-wrap">
            {error.message}
          </pre>
        ) : null}
        <div className="mt-5 flex items-center justify-end gap-3">
          <a href="/home" className="link-quiet text-[12px]">
            back home
          </a>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn btn-spell btn-sm"
          >
            reload
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </div>
    </main>
  );
}
